/**
 * Раздел «Склад» портала — этап P5 (§2 ТЗ портала).
 *
 * Три вкладки:
 *
 * - **Аудит** — товары без единиц и пересчётов, минусовые остатки, дубли
 *   (право `ops.stock_audit`). Агент ничего не меняет — правят в складской
 *   программе.
 * - **Цены закупки** — подорожание по истории закупок. Это деньги
 *   поставщикам, поэтому вкладка — праву «Оплаты».
 * - **Блюда и товары** — выбор товара склада для блюда кассы (финансовый
 *   агент, право «Финансы»).
 *
 * Адреса: `/stock`, `/stock/prices`, `/stock/dishes`.
 */

import { Denied } from '@shell/states';
import type { Navigation } from '../../api';
import { navigate, useRoute } from '../../router';
import { Audit } from './Audit';
import { Dishes } from './Dishes';
import { Prices } from './Prices';
import './stock.css';

export type StockTab = 'audit' | 'prices' | 'dishes';

const TABS: { code: StockTab; title: string; path: string }[] = [
  { code: 'audit', title: 'Аудит', path: '/stock' },
  { code: 'prices', title: 'Цены закупки', path: '/stock/prices' },
  { code: 'dishes', title: 'Блюда и товары', path: '/stock/dishes' },
];

export function stockTabs(nav: Navigation): StockTab[] {
  const host = nav.user.is_host;
  const ops = nav.services.ops?.rights ?? {};
  const finance = nav.services.finance?.rights ?? {};
  return TABS.filter((tab) => {
    if (host) return true;
    if (tab.code === 'audit') return Boolean(ops.stock_audit);
    if (tab.code === 'prices') return Boolean(ops.stock_prices);
    return Boolean(finance.stock_dishes);
  }).map((tab) => tab.code);
}

export function readStock(path: string): StockTab | null {
  if (path.startsWith('/stock/prices')) return 'prices';
  if (path.startsWith('/stock/dishes')) return 'dishes';
  return path === '/stock' || path === '/stock/audit' ? 'audit' : null;
}

export default function Stock({ nav }: { nav: Navigation }) {
  const route = useRoute();
  const tabs = stockTabs(nav);
  // Меню показывает раздел по «Аудиту склада»; по прямой ссылке открываются
  // и вкладки других прав — если есть хоть одна.
  if (tabs.length === 0) return <Denied />;
  const asked = readStock(route.path);
  const tab = asked && tabs.includes(asked) ? asked : tabs[0];

  return (
    <div className="page stock-page">
      <header className="page__head stock-page__head">
        <h1 className="page__title">Склад</h1>
      </header>
      <div className="chips stock-tabs" role="tablist" aria-label="Склад">
        {TABS.filter((item) => tabs.includes(item.code)).map((item) => (
          <button
            key={item.code}
            type="button"
            role="tab"
            aria-selected={item.code === tab}
            className={`chip${item.code === tab ? ' chip--active' : ''}`}
            onClick={() => navigate(item.path)}
          >
            {item.title}
          </button>
        ))}
      </div>
      {tab === 'audit' ? <Audit /> : tab === 'prices' ? <Prices /> : <Dishes />}
    </div>
  );
}
